/**
 * Small, dependency-free replacements for the handful of lodash collection
 * helpers this monorepo actually uses. Each is a faithful reimplementation of
 * the lodash semantics that the real call sites depend on (stable non-mutating
 * sorts, `capitalize` lowercasing the tail, `max`/`min` skipping NaN and
 * returning `undefined` for empty input, `maxBy`/`minBy` returning the element,
 * `merge` being a deep mutating merge, `uniqBy` keeping the first occurrence).
 *
 * Only the behavior exercised by the codebase is reproduced — these are not
 * general-purpose lodash clones. See collections.test.ts for the parity cases.
 */

/** A key selector: either a function or a property name (mirrors lodash's shorthand iteratee). */
type Iteratee<T> = ((item: T) => PropertyKey | null | undefined) | keyof T;

function keyFn<T>(iteratee: Iteratee<T>): (item: T) => PropertyKey | null | undefined {
  if (typeof iteratee === 'function') return iteratee;
  return (item: T) => {
    const value = item[iteratee];
    // lodash coerces computed keys with String() (except symbols, which pass through).
    return typeof value === 'symbol' ? value : String(value);
  };
}

/**
 * Ascending comparison matching lodash's `compareAscending` for the value types
 * this codebase sorts by (number, string, Date). Ordering of "missing" values:
 * defined < null < undefined. `orderBy`/`sortBy` build on this; `desc` simply
 * negates the result (which, as in lodash, also flips where null/undefined land).
 */
function baseCompare(a: unknown, b: unknown): number {
  if (a === b) return 0;
  if (a === undefined) return 1;
  if (b === undefined) return -1;
  if (a === null) return 1;
  if (b === null) return -1;
  if (a instanceof Date && b instanceof Date) {
    const diff = a.getTime() - b.getTime();
    return diff < 0 ? -1 : diff > 0 ? 1 : 0;
  }
  if (typeof a === 'number' && typeof b === 'number') {
    return a < b ? -1 : a > b ? 1 : 0;
  }
  if (typeof a === 'string' && typeof b === 'string') {
    return a < b ? -1 : a > b ? 1 : 0;
  }
  // Non-comparable values (e.g. plain objects) sort equal — preserving the
  // input's relative order, which (with a stable sort) matches lodash here.
  return 0;
}

/**
 * Uppercase the first character and lowercase the rest — lodash `capitalize`.
 * The tail-lowercasing is load-bearing: `capitalize('CFP Advisor') === 'Cfp advisor'`.
 * Empty string returns ''. Does not trim (matching lodash).
 */
export function capitalize(str: string): string {
  return str.charAt(0).toUpperCase() + str.slice(1).toLowerCase();
}

/**
 * Split `array` into consecutive sub-arrays of length `size` (final chunk holds
 * the remainder). A non-positive/non-integer `size` or empty input yields `[]`.
 * Non-mutating; chunks are shallow slices of the source.
 */
export function chunk<T>(array: readonly T[], size: number): T[][] {
  const chunkSize = Math.max(Math.floor(size), 0);
  if (chunkSize < 1 || array.length === 0) return [];
  const result: T[][] = [];
  for (let i = 0; i < array.length; i += chunkSize) {
    result.push(array.slice(i, i + chunkSize));
  }
  return result;
}

/**
 * Group items by the key produced by `iteratee` (a function or property name).
 * Keys are coerced with String(), matching lodash. Preserves insertion order
 * within each group. `null`/`undefined` input yields `{}`.
 */
export function groupBy<T>(array: readonly T[] | null | undefined, iteratee: Iteratee<T>): Record<string, T[]> {
  const fn = keyFn(iteratee);
  const result: Record<string, T[]> = {};
  if (array == null) return result;
  for (const item of array) {
    const key = String(fn(item));
    (result[key] ??= []).push(item);
  }
  return result;
}

/**
 * Index items by the key produced by `iteratee` (a function or property name).
 * On duplicate keys the last item wins (lodash `keyBy`). `null`/`undefined`
 * input yields `{}`.
 */
export function keyBy<T>(array: readonly T[] | null | undefined, iteratee: Iteratee<T>): Record<string, T> {
  const fn = keyFn(iteratee);
  const result: Record<string, T> = {};
  if (array == null) return result;
  for (const item of array) {
    result[String(fn(item))] = item;
  }
  return result;
}

/**
 * Map an object's values through `iteratee`, preserving keys — lodash `mapValues`.
 * Works for both string- and number-keyed records (the two casts bridge
 * `Object.keys`'s widened `string[]` and index-access widening).
 */
export function mapValues<T extends object, U>(obj: T, iteratee: (value: T[keyof T], key: string) => U): { [K in keyof T]: U } {
  const result = {} as { [K in keyof T]: U };
  for (const key of Object.keys(obj) as (keyof T)[]) {
    result[key] = iteratee(obj[key] as T[keyof T], String(key));
  }
  return result;
}

/** A sort selector: function or property name. */
type SortIteratee<T> = ((item: T) => unknown) | keyof T;

function sortValue<T>(iteratee: SortIteratee<T>, item: T): unknown {
  return typeof iteratee === 'function' ? iteratee(item) : item[iteratee];
}

/**
 * Stable, non-mutating ascending sort by one or more selectors — lodash `sortBy`.
 * With no selector, sorts by identity. `null`/`undefined` input yields `[]`.
 */
export function sortBy<T>(
  array: readonly T[] | null | undefined,
  iteratee?: SortIteratee<T> | ReadonlyArray<SortIteratee<T>>,
): T[] {
  if (array == null) return [];
  const iteratees: ReadonlyArray<SortIteratee<T>> =
    iteratee == null ? [(x: T) => x] : Array.isArray(iteratee) ? iteratee : [iteratee];
  return [...array].sort((a, b) => {
    for (const it of iteratees) {
      const cmp = baseCompare(sortValue(it, a), sortValue(it, b));
      if (cmp !== 0) return cmp;
    }
    return 0;
  });
}

/**
 * Stable, non-mutating sort by one or more selectors with per-selector direction
 * — lodash `orderBy`. Directions default to `'asc'`; a missing direction is
 * `'asc'`. As in lodash, `'desc'` negates the comparison (so null/undefined,
 * which sort last ascending, sort first descending). `null`/`undefined` input
 * yields `[]`.
 */
export function orderBy<T>(
  array: readonly T[] | null | undefined,
  iteratees: SortIteratee<T> | ReadonlyArray<SortIteratee<T>>,
  orders?: ReadonlyArray<'asc' | 'desc'>,
): T[] {
  if (array == null) return [];
  const its: ReadonlyArray<SortIteratee<T>> = Array.isArray(iteratees) ? iteratees : [iteratees];
  return [...array].sort((a, b) => {
    for (let i = 0; i < its.length; i++) {
      const it = its[i];
      if (it === undefined) continue;
      const cmp = baseCompare(sortValue(it, a), sortValue(it, b));
      if (cmp !== 0) return orders?.[i] === 'desc' ? -cmp : cmp;
    }
    return 0;
  });
}

/**
 * Dedupe by the key produced by `iteratee`, keeping the FIRST occurrence and
 * preserving order — lodash `uniqBy`. `null`/`undefined` input yields `[]`.
 */
export function uniqBy<T>(array: readonly T[] | null | undefined, iteratee: Iteratee<T>): T[] {
  if (array == null) return [];
  const fn = keyFn(iteratee);
  const seen = new Set<unknown>();
  const result: T[] = [];
  for (const item of array) {
    const key = fn(item);
    if (!seen.has(key)) {
      seen.add(key);
      result.push(item);
    }
  }
  return result;
}

/**
 * Return the element with the maximum `iteratee` value — lodash `maxBy`. Skips
 * `null`/`undefined`/`NaN` iteratee results, keeps the first element on ties,
 * and returns `undefined` for empty input. Compares numbers numerically, strings
 * lexicographically, and Dates chronologically.
 */
export function maxBy<T>(array: readonly T[] | null | undefined, iteratee: (item: T) => number | string | Date | null | undefined): T | undefined {
  if (array == null) return undefined;
  let result: T | undefined;
  let computed: number | string | Date | undefined;
  for (const item of array) {
    const value = iteratee(item);
    if (value == null || (typeof value === 'number' && Number.isNaN(value))) continue;
    if (computed === undefined || baseCompare(value, computed) > 0) {
      computed = value;
      result = item;
    }
  }
  return result;
}

/** Return the element with the minimum `iteratee` value — lodash `minBy`. See {@link maxBy}. */
export function minBy<T>(array: readonly T[] | null | undefined, iteratee: (item: T) => number | string | Date | null | undefined): T | undefined {
  if (array == null) return undefined;
  let result: T | undefined;
  let computed: number | string | Date | undefined;
  for (const item of array) {
    const value = iteratee(item);
    if (value == null || (typeof value === 'number' && Number.isNaN(value))) continue;
    if (computed === undefined || baseCompare(value, computed) < 0) {
      computed = value;
      result = item;
    }
  }
  return result;
}

/**
 * Maximum of a list of numbers or Dates — lodash `max`. Skips `null`/`undefined`
 * and NaN, and returns `undefined` for empty input (unlike `Math.max`, which
 * returns `-Infinity` and is poisoned by NaN / non-number values). Accepts arrays
 * whose elements may be nullish (like lodash) and returns the non-null element type.
 */
export function max<T extends number | Date>(array: ReadonlyArray<T | null | undefined> | null | undefined): T | undefined {
  if (array == null) return undefined;
  let result: T | undefined;
  for (const value of array) {
    if (value == null || (typeof value === 'number' && Number.isNaN(value))) continue;
    if (result === undefined || baseCompare(value, result) > 0) result = value;
  }
  return result;
}

/** Minimum of a list of numbers or Dates — lodash `min`. See {@link max}. */
export function min<T extends number | Date>(array: ReadonlyArray<T | null | undefined> | null | undefined): T | undefined {
  if (array == null) return undefined;
  let result: T | undefined;
  for (const value of array) {
    if (value == null || (typeof value === 'number' && Number.isNaN(value))) continue;
    if (result === undefined || baseCompare(value, result) < 0) result = value;
  }
  return result;
}

/**
 * Shallow clone of `obj` without the given key(s) — lodash `omit` (flat keys only).
 * Accepts a single key or an array of keys.
 */
export function omit<T extends object, K extends keyof T>(obj: T, keys: K | readonly K[]): Omit<T, K> {
  const toOmit = new Set<PropertyKey>(Array.isArray(keys) ? keys : [keys]);
  const result: Partial<T> = {};
  for (const key of Object.keys(obj) as (keyof T)[]) {
    if (!toOmit.has(key)) result[key] = obj[key];
  }
  return result as Omit<T, K>;
}

/**
 * Shallow clone of `obj` keeping only entries for which `predicate` returns true
 * — lodash `pickBy`. Iterates own enumerable string keys.
 */
export function pickBy<T>(obj: Record<string, T>, predicate: (value: T, key: string) => boolean): Record<string, T> {
  const result: Record<string, T> = {};
  for (const [key, value] of Object.entries(obj)) {
    if (predicate(value, key)) result[key] = value;
  }
  return result;
}

/**
 * Shallow clone of `obj` dropping entries for which `predicate` returns true
 * — lodash `omitBy` (the inverse of {@link pickBy}). Iterates own enumerable
 * string keys.
 */
export function omitBy<T>(obj: Record<string, T>, predicate: (value: T, key: string) => boolean): Record<string, T> {
  const result: Record<string, T> = {};
  for (const [key, value] of Object.entries(obj)) {
    if (!predicate(value, key)) result[key] = value;
  }
  return result;
}

function isPlainObject(value: unknown): value is Record<string, unknown> {
  if (typeof value !== 'object' || value === null) return false;
  const proto: unknown = Object.getPrototypeOf(value);
  return proto === Object.prototype || proto === null;
}

// Typed guard so narrowing yields `unknown[]` rather than `Array.isArray`'s `any[]`.
function isUnknownArray(value: unknown): value is unknown[] {
  return Array.isArray(value);
}

function mergeInto(target: Record<string, unknown>, source: Record<string, unknown>): void {
  for (const key of Object.keys(source)) {
    const sourceVal = source[key];
    // lodash `merge` skips `undefined` source values (never overwrites with undefined).
    if (sourceVal === undefined) continue;
    const targetVal = target[key];
    if (isUnknownArray(sourceVal)) {
      const base: unknown[] = isUnknownArray(targetVal) ? targetVal : [];
      // Arrays merge by index, recursively — matching lodash. Treating an array as a
      // Record with numeric-string keys gives index-wise merge for free.
      mergeInto(base as unknown as Record<string, unknown>, sourceVal as unknown as Record<string, unknown>);
      target[key] = base;
    } else if (isPlainObject(sourceVal)) {
      // A plain-object source merges its keys onto an existing object OR array
      // target (lodash keeps the array and assigns the object's keys onto it);
      // only a non-object/array target starts fresh from {}.
      const base: Record<string, unknown> | unknown[] =
        isPlainObject(targetVal) || isUnknownArray(targetVal) ? targetVal : {};
      mergeInto(base as unknown as Record<string, unknown>, sourceVal);
      target[key] = base;
    } else {
      target[key] = sourceVal;
    }
  }
}

/**
 * Deep merge of `sources` into `target`, mutating and returning `target` —
 * the plain-object/array subset of lodash `merge`. Recurses into plain objects
 * and merges arrays by index; skips `undefined` source values; overwrites with
 * any other value type. A `null`/`undefined` target starts from a fresh object
 * (as lodash's `Object(undefined)` does).
 *
 * Not a general-purpose merge: does not handle Maps/Sets/class instances/
 * customizers. Only the shapes merged by the call sites (LinkedIn/SalesNav
 * profile JSON) are supported.
 */
export function merge<T extends object>(target: T | undefined | null, ...sources: object[]): T {
  const base: Record<string, unknown> = (target ?? {}) as Record<string, unknown>;
  for (const source of sources) {
    if (source != null) mergeInto(base, source as Record<string, unknown>);
  }
  return base as T;
}
